import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Lock } from 'lucide-react';

import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Avatar } from '@/components/community/Avatar';
import { PostFeed } from '@/components/community/PostFeed';
import { ProfileHeader } from '@/components/community/ProfileHeader';
import { SharedRecipeCard, SharedRoutineCard } from '@/components/community/PostAttachment';
import { SharedGrid } from '@/components/community/SharedGrid';
import { profileSchema } from '@/components/community/searchParams';
import {
  usePublicProfile,
  useUserPosts,
  useUserRecipes,
  useUserRoutines,
} from '@/lib/queries/users';

export const Route = createFileRoute('/_app/community/u/$handle/')({
  validateSearch: profileSchema,
  component: PublicProfilePage,
});

/**
 * Perfil público de una cuenta.
 *
 * Una cuenta no pública responde con la misma forma pero vacía —sin bio y con
 * los contadores a cero—, así que aquí no hay que filtrar nada: basta con
 * enseñar la tarjeta de «perfil privado» en lugar de las pestañas.
 */
function PublicProfilePage() {
  const { handle } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const query = usePublicProfile(handle);
  const profile = query.data;

  const visible = profile !== undefined && (profile.isPublic || profile.viewer.isSelf);
  const active = tab ?? 'posts';

  const posts = useUserPosts(handle, visible && active === 'posts');
  const routines = useUserRoutines(handle, visible && active === 'routines');
  const recipes = useUserRecipes(handle, visible && active === 'recipes');

  return (
    <>
      <PageHeader
        title={profile?.displayName ?? `@${handle}`}
        action={
          <Link
            to="/community"
            search={{}}
            aria-label="Volver a la comunidad"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando el perfil…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos encontrado esta cuenta"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-5">
          <ProfileHeader profile={query.data} />

          {!visible ? (
            <section className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-line bg-surface-raised px-5 py-8 text-center">
              <Lock className="size-7 text-ink-faint" aria-hidden="true" />
              <Avatar url={query.data.avatarUrl} name={query.data.displayName} />
              <p className="text-base font-semibold text-ink">{query.data.displayName}</p>
              <p className="text-sm text-brand">@{query.data.handle}</p>
              <p className="mt-1 text-sm text-ink-muted">
                Perfil privado. Esta cuenta no comparte publicaciones, rutinas ni recetas.
              </p>
            </section>
          ) : (
            <>
              <div
                role="tablist"
                aria-label="Contenido del perfil"
                className="flex gap-1 rounded-xl border border-line bg-surface-raised p-1"
              >
                <ProfileTab
                  selected={active === 'posts'}
                  onClick={() => void navigate({ search: {}, replace: true })}
                >
                  Publicaciones
                </ProfileTab>
                <ProfileTab
                  selected={active === 'routines'}
                  onClick={() => void navigate({ search: { tab: 'routines' }, replace: true })}
                >
                  Rutinas
                </ProfileTab>
                <ProfileTab
                  selected={active === 'recipes'}
                  onClick={() => void navigate({ search: { tab: 'recipes' }, replace: true })}
                >
                  Recetas
                </ProfileTab>
              </div>

              {active === 'posts' ? (
                <PostFeed
                  posts={posts.data?.pages.flatMap((page) => page.items) ?? []}
                  isPending={posts.isPending}
                  isError={posts.isError}
                  error={posts.error}
                  onRetry={() => void posts.refetch()}
                  hasNextPage={posts.hasNextPage}
                  isFetchingNextPage={posts.isFetchingNextPage}
                  onLoadMore={() => void posts.fetchNextPage()}
                  empty={<Empty>Esta cuenta todavía no ha publicado nada.</Empty>}
                />
              ) : active === 'routines' ? (
                <SharedGrid
                  items={routines.data?.pages.flatMap((page) => page.items) ?? []}
                  isPending={routines.isPending}
                  isError={routines.isError}
                  error={routines.error}
                  onRetry={() => void routines.refetch()}
                  hasNextPage={routines.hasNextPage}
                  isFetchingNextPage={routines.isFetchingNextPage}
                  onLoadMore={() => void routines.fetchNextPage()}
                  emptyText="Esta cuenta no tiene rutinas públicas."
                  errorTitle="No hemos podido cargar las rutinas"
                  render={(routine) => <SharedRoutineCard routine={routine} />}
                />
              ) : (
                <SharedGrid
                  items={recipes.data?.pages.flatMap((page) => page.items) ?? []}
                  isPending={recipes.isPending}
                  isError={recipes.isError}
                  error={recipes.error}
                  onRetry={() => void recipes.refetch()}
                  hasNextPage={recipes.hasNextPage}
                  isFetchingNextPage={recipes.isFetchingNextPage}
                  onLoadMore={() => void recipes.fetchNextPage()}
                  emptyText="Esta cuenta no tiene recetas públicas."
                  errorTitle="No hemos podido cargar las recetas"
                  render={(recipe) => <SharedRecipeCard recipe={recipe} />}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

function ProfileTab({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={
        selected
          ? 'min-h-11 flex-auto rounded-lg bg-brand px-2 text-sm font-semibold text-brand-ink xs:flex-1'
          : 'min-h-11 flex-auto rounded-lg px-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-overlay active:bg-surface-overlay xs:flex-1'
      }
    >
      {children}
    </button>
  );
}

function Empty({ children }: { children: string }) {
  return (
    <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-5 py-10 text-center text-sm text-ink-muted">
      {children}
    </p>
  );
}
